// Kortet: blødt hav med små bølger, land med blød skygge og lyst kystbånd, træer og marker.
// Ligger i verdenskoordinater (km) under kameraets transform.

import { Container, Graphics, Sprite } from 'pixi.js';
import { COASTLINE } from '../content/map';
import type { Atlas } from './atlas';
import type { Rect } from './camera';
import { pointInPoly, prng, smoothClosed } from './geo';
import type { P } from './geo';
import { C, mix } from './palette';
import type { View } from './view';

/** Markernes toner: eng, kløver, korn og en lys mark. */
const FIELD_TONES = [0xd9eedb, 0xcde9d6, 0xf2edcc, 0xe3f2dc] as const;

interface Wave {
  s: Sprite;
  x: number;
  y: number;
  phase: number;
}

export class MapLayer {
  readonly world = new Container();
  private land = new Graphics();
  private decor = new Container();
  private waves: Wave[] = [];
  private polys: P[][] = [];
  /** Det lodrette stræk, træer og bølger er rettet til. */
  private ky = 1;

  constructor(private atlas: Atlas) {
    this.world.addChild(this.land, this.decor);
    this.polys = COASTLINE.map((c) => smoothClosed(c.points, 6));
  }

  /** Tegn kortet for en bane. `px` er hvor mange km én skærm-px er ved fit. */
  build(bounds: Rect, fitScale: number, nodes: P[], seed: number, decor: boolean): void {
    const px = 1 / fitScale;
    const g = this.land;
    g.clear();
    // Hav i hele udsnittet plus god margen (baggrundsfarven dækker resten).
    const m = Math.max(bounds.w, bounds.h);
    g.rect(bounds.x - m, bounds.y - m, bounds.w + m * 2, bounds.h + m * 2).fill(C.sea);
    // Lavt vand langs kysten: to bånd, der bliver lysere ind mod land. Kysten er lang og tegnes
    // hver frame, så lagene holdes få og leddene skrå (runde led på brede streger koster meget).
    for (const poly of this.polys) {
      g.poly(poly, true).stroke({ width: 56 * px, color: C.seaShallow, alpha: 0.5, join: 'bevel' });
      g.poly(poly, true).stroke({ width: 24 * px, color: C.white, alpha: 0.3, join: 'bevel' });
    }
    // Landet er en tyk, udstanset plade: en kant med skum ved foden.
    const drop = (dx: number, dy: number) => (poly: P[]) => poly.map((p) => ({ x: p.x + dx * px, y: p.y + dy * px }));
    for (const poly of this.polys.map(drop(0, 7))) {
      g.poly(poly, true).fill(C.landSide).stroke({ width: 2.5 * px, color: C.white, alpha: 0.8, join: 'round' });
    }
    for (const poly of this.polys) {
      g.poly(poly, true).fill(C.land).stroke({ width: 2 * px, color: C.landRim, join: 'round' });
    }
    this.decor.removeChildren().forEach((c) => c.destroy());
    this.waves = [];
    this.ky = 1;
    const rnd = prng(seed * 7919 + 17);
    // Tætheden regnes i skærmpixels, så kortet ser ens ud uanset zoom og bane.
    const areaPx = (bounds.w / px) * (bounds.h / px);
    // Marker som et kludetæppe: små klynger af skæve firkanter med samme retning og fure,
    // i lyse toner, så noder, ledninger og prikker altid står foran.
    const fields = new Graphics();
    for (let i = 0; i < areaPx / 22000; i++) {
      const c = { x: bounds.x + rnd() * bounds.w, y: bounds.y + rnd() * bounds.h };
      const ang = (rnd() - 0.5) * 0.9;
      const ca = Math.cos(ang);
      const sa = Math.sin(ang);
      const cell = (20 + rnd() * 10) * px;
      const cols = 2 + Math.floor(rnd() * 3);
      const rows = 1 + Math.floor(rnd() * 3);
      for (let r = 0; r < rows; r++) {
        for (let q = 0; q < cols; q++) {
          if (rnd() < 0.2) continue;
          const w = cell * (0.8 + rnd() * 0.5);
          const h = cell * (0.55 + rnd() * 0.3);
          const lx = (q - cols / 2) * cell * 1.12;
          const ly = (r - rows / 2) * cell * 0.82;
          const corners = [
            [lx, ly],
            [lx + w, ly],
            [lx + w, ly + h],
            [lx, ly + h],
          ].map(([x, y]) => ({ x: c.x + (x as number) * ca - (y as number) * sa, y: c.y + (x as number) * sa + (y as number) * ca }));
          if (!corners.every((p) => this.onLand(p))) continue;
          const tone = FIELD_TONES[Math.floor(rnd() * FIELD_TONES.length)] as number;
          fields.poly(corners, true).fill(tone);
          // Furer på langs.
          const [a, b, , d] = corners as [P, P, P, P];
          for (let f = 1; f < 4; f++) {
            const t = f / 4;
            fields.moveTo(a.x + (d.x - a.x) * t, a.y + (d.y - a.y) * t).lineTo(b.x + (d.x - a.x) * t, b.y + (d.y - a.y) * t);
          }
          fields.stroke({ width: 1.2 * px, color: mix(tone, C.treeDark, 0.14), alpha: 0.45 });
        }
      }
    }
    // Bløde enge mellem markerne.
    for (let i = 0; i < areaPx / 30000; i++) {
      const p = { x: bounds.x + rnd() * bounds.w, y: bounds.y + rnd() * bounds.h };
      if (!this.onLand(p)) continue;
      const rx = (16 + rnd() * 20) * px;
      fields.ellipse(p.x, p.y, rx, rx * (0.45 + rnd() * 0.25)).fill({ color: C.grass, alpha: 0.28 });
    }
    this.decor.addChild(fields);
    if (decor) {
      // Små lunde frem for drys: roligere at se på, og noder og ledninger står frit.
      const treeScale = px * 0.95;
      for (let i = 0; i < areaPx / 42000; i++) {
        const c = { x: bounds.x + rnd() * bounds.w, y: bounds.y + rnd() * bounds.h };
        const n = 2 + Math.floor(rnd() * 5);
        for (let j = 0; j < n; j++) {
          const a = rnd() * Math.PI * 2;
          const r = rnd() * 22 * px;
          const p = { x: c.x + Math.cos(a) * r, y: c.y + Math.sin(a) * r * 0.6 };
          if (!this.onLand(p)) continue;
          if (nodes.some((q) => Math.hypot(q.x - p.x, q.y - p.y) < 48 * px)) continue;
          const s = this.atlas.sprite(rnd() < 0.6 ? 'tree0' : 'tree1');
          s.position.set(p.x, p.y);
          s.scale.set(treeScale * (0.75 + rnd() * 0.45));
          this.decor.addChild(s);
        }
      }
      for (let i = 0; i < areaPx / 22000; i++) {
        const p = { x: bounds.x - 10 * px + rnd() * (bounds.w + 20 * px), y: bounds.y - 10 * px + rnd() * (bounds.h + 20 * px) };
        if (this.nearLand(p, 14 * px)) continue;
        const s = this.atlas.sprite('wave');
        s.scale.set(px * 1.3);
        s.alpha = 0.7;
        this.decor.addChild(s);
        this.waves.push({ s, x: p.x, y: p.y, phase: rnd() * Math.PI * 2 });
      }
    }
    // Træer sorteres, så dem længst mod syd står forrest.
    this.decor.children.sort((a, b) => a.y - b.y);
  }

  onLand(p: P): boolean {
    return this.polys.some((poly) => pointInPoly(p, poly));
  }

  private nearLand(p: P, r: number): boolean {
    if (this.onLand(p)) return true;
    for (const [dx, dy] of [
      [r, 0],
      [-r, 0],
      [0, r],
      [0, -r],
    ] as const) {
      if (this.onLand({ x: p.x + dx, y: p.y + dy })) return true;
    }
    return false;
  }

  update(v: View): void {
    const c = v.cam;
    this.world.position.set(c.ox, c.oy);
    this.world.scale.set(c.scale, c.scale * c.ky);
    // Kortet strækkes på højden i højformat, men træer og bølger skal ikke blive lange.
    if (c.ky !== this.ky) {
      this.ky = c.ky;
      for (const s of this.decor.children) if (s instanceof Sprite) s.scale.y = s.scale.x / c.ky;
    }
    if (v.calm) return;
    const t = v.now / 1000;
    for (const w of this.waves) {
      const k = Math.sin(t * 0.9 + w.phase);
      w.s.position.set(w.x + Math.sin(t * 0.4 + w.phase) * 0.8, w.y + k * 0.25);
      w.s.alpha = 0.35 + 0.35 * (0.5 + 0.5 * k);
    }
  }
}
